'use client'

// What drops out of his vending machine, made a big deal of (28 Oct). Sam: "make a bit of a big deal out of the item
// coming out the machine when bought - perhaps it shows up on screen spinning or something like that...kind of
// playstation UX vibe".
//
// While the webhook draws it, the spiral turns and the corner says NOW LOADING. When it lands, the screen flashes, the
// thing rises into the middle and turns there, and a blue window with a white rim names it, with ✕ to close. A row
// bought for him is the thing itself, cut from the machine's own model (public/models/vend_items.glb, made by
// scripts/vend-items.mjs, Draco and KTX2 like every model); a print is a card that spins round to his drawing; a pack of stickers is six on a ring.
//
// The thing is drawn in a little canvas of its own, plain WebGL, only while the drop is up: the world's renderer is
// left alone. It is drawn the way a PlayStation drew: a low buffer shown in big pixels, the corners snapped to a
// coarse grid so it wobbles as it turns, the texture laid on without perspective so it swims, and fifteen-bit colour
// with an ordered dither.

import { useEffect, useRef, useState } from 'react'
import * as THREE from 'three'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js'
import { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js'
import { getCtx } from '../../lib/audio/context'
import { DRACO_PATH, BASIS_PATH, bufferedKTX2 } from '../three/hooks/useModel'

const ITEMS_URL = '/models/vend_items.glb'
// the row a thing came out of, and its node in the items model (the disks are the colour each column holds)
const NODE_OF = {
  A1: 'energy', A2: 'crisps', A3: 'choc', A4: 'worms', B1: 'noodles', B2: 'water',
  C1: 'spool', C2: 'spray', C3: 'camera', D1: 'disk_blue', D2: 'disk_black', D3: 'disk_grey', D4: 'disk_red',
}
const BUFFER_H = 208 // the height it is drawn at, before it is blown up
const SETTLE_S = 2.4 // a print's turns, to where it faces you
const PRINT_SCALE = 1.3 // a print fills the stage: it is the thing they bought
// how tall the print stands on the stage once it faces you, as a share of the stage (the camera's 32 degrees at 5.2)
const PRINT_TALL = (1.3 * 1.4 * PRINT_SCALE) / (2 * 5.2 * Math.tan((16 * Math.PI) / 180))

// the items load the way every model does (components/three/hooks/useModel.js): Draco geometry and KTX2 textures,
// through the site's own decoder and transcoder, the KTX2 straight from the model's buffer to the transcoder's workers.
// The transcoder is asked what this canvas's WebGL can take (the world's loader asked its WebGPU), and let go once the
// model is in, so no second pool of workers is left running
let itemsGltf = null
const loadItems = (renderer) => (itemsGltf ||= (async () => {
  const ktx2 = new KTX2Loader().setTranscoderPath(BASIS_PATH).detectSupport(renderer)
  const draco = new DRACOLoader().setDecoderPath(DRACO_PATH)
  const loader = new GLTFLoader().setDRACOLoader(draco).setKTX2Loader(ktx2)
  loader.register(bufferedKTX2)
  try { return await loader.loadAsync(ITEMS_URL) } finally { ktx2.dispose(); draco.dispose() }
})().catch((err) => { itemsGltf = null; throw err }))
// the grid the corners land on, one for every material on the stage (a card's picture arrives after the stage is sized)
const SNAP = new THREE.Vector2(60, 60)

const VERT = /* glsl */ `
uniform vec2 uSnap;
uniform vec4 uUv;
varying vec3 vUvw;
varying float vShade;
void main() {
  vec4 clip = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  // the corners land on a coarse grid, as the PlayStation's did: it wobbles as it turns
  clip.xy = floor(clip.xy / clip.w * uSnap + 0.5) / uSnap * clip.w;
  gl_Position = clip;
  // and the texture is laid on in screen space, without perspective, so it swims
  vUvw = vec3((uv * uUv.xy + uUv.zw) * clip.w, clip.w);
  vec3 n = normalize(normalMatrix * normal);
  vShade = 0.55 + 0.6 * max(dot(n, normalize(vec3(-0.45, 0.55, 0.75))), 0.0);
}`

const FRAG = /* glsl */ `
uniform sampler2D map;
uniform vec3 uTint;
uniform float uFill;
varying vec3 vUvw;
varying float vShade;
float bayer2(vec2 a) { a = floor(a); return fract(dot(a, vec2(0.5, a.y * 0.75))); }
float bayer4(vec2 a) { return bayer2(0.5 * a) * 0.25 + bayer2(a); }
void main() {
  vec4 t = texture2D(map, vUvw.xy / vUvw.z);
  if (t.a < 0.5) discard;
  // (uFill: the white backing of a die-cut sticker, its own shape and none of its picture)
  vec3 c = mix(t.rgb * uTint, vec3(0.93), uFill) * vShade;
  // fifteen-bit colour, dithered
  c = floor(c * 31.0 + bayer4(gl_FragCoord.xy)) / 31.0;
  gl_FragColor = vec4(clamp(c, 0.0, 1.0), 1.0);
}`

// (his bag's pictures draw the same way, on a stage of their own size with a grid of their own: components/ui/bagPictures.js)
export function psx(map, tint = new THREE.Color(1, 1, 1), uv = new THREE.Vector4(1, 1, 0, 0), fill = 0, snap = SNAP) {
  let tex = map
  if (!tex) {
    tex = new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1)
    tex.needsUpdate = true
  }
  // its bytes as they are, nearest: the colour is handled by hand, and the pixels are the point
  tex.colorSpace = THREE.NoColorSpace
  tex.magFilter = THREE.NearestFilter
  tex.minFilter = THREE.NearestFilter
  tex.generateMipmaps = false
  tex.needsUpdate = true
  return new THREE.ShaderMaterial({
    uniforms: { map: { value: tex }, uTint: { value: tint }, uSnap: { value: snap }, uUv: { value: uv }, uFill: { value: fill } },
    vertexShader: VERT,
    fragmentShader: FRAG,
  })
}

// the back of a card: his purple in stripes, and his name
function cardBack() {
  const c = document.createElement('canvas')
  c.width = 64; c.height = 90
  const g = c.getContext('2d')
  g.fillStyle = '#17141e'; g.fillRect(0, 0, 64, 90)
  g.strokeStyle = '#7c5cff'; g.lineWidth = 4
  for (let i = -90; i < 160; i += 12) { g.beginPath(); g.moveTo(i, 0); g.lineTo(i + 90, 90); g.stroke() }
  g.fillStyle = '#17141e'; g.fillRect(10, 32, 44, 26)
  g.fillStyle = '#fff'; g.font = 'bold 18px sans-serif'; g.textAlign = 'center'; g.textBaseline = 'middle'
  g.fillText('ピノ', 32, 46)
  return new THREE.CanvasTexture(c)
}

// a picture of his laid on a card the way object-fit: cover would
function coverUv(img, w, h) {
  const a = img.width / img.height
  const b = w / h
  return a > b ? new THREE.Vector4(b / a, 1, (1 - b / a) / 2, 0) : new THREE.Vector4(1, a / b, 0, (1 - a / b) / 2)
}

// the print as it comes (Sam, 28 Oct: "the prints are different to the image you've shown me"): his drawing is a square
// on paper, and the print is that square in the middle of a sheet one and four tenths as tall, the paper going on above
// and below it. The sheet is made here the same way, the paper its own colour off the drawing's corners
export const SHEET = 1.4
const loadImage = (src) => new Promise((resolve, reject) => { const i = new Image(); i.onload = () => resolve(i); i.onerror = reject; i.src = src })
export async function printSheet(src, width = 500) {
  const img = await loadImage(src)
  const c = document.createElement('canvas')
  c.width = width
  c.height = Math.round(width * SHEET)
  const g = c.getContext('2d')
  let paper = '#e6e1d6'
  try {
    const probe = document.createElement('canvas')
    probe.width = img.width; probe.height = img.height
    const p = probe.getContext('2d')
    p.drawImage(img, 0, 0)
    const px = [[2, 2], [img.width - 3, 2], [2, img.height - 3], [img.width - 3, img.height - 3]].map(([x, y]) => p.getImageData(x, y, 1, 1).data)
    paper = `rgb(${[0, 1, 2].map((k) => Math.round(px.reduce((a, d) => a + d[k], 0) / px.length)).join(',')})`
  } catch { /* the paper we know */ }
  g.fillStyle = paper
  g.fillRect(0, 0, c.width, c.height)
  g.drawImage(img, 0, (c.height - width) / 2, width, width)
  return c
}

function card(src, w, h, back, sheet = null, { die = false } = {}) {
  const g = new THREE.Group()
  const geo = new THREE.PlaneGeometry(w, h, 4, 6) // cut up, as the PlayStation cut up big faces, so the swim stays small
  const front = new THREE.Mesh(geo, psx(null))
  front.position.z = 0.012
  g.add(front)
  const rear = new THREE.Mesh(geo, psx(back))
  rear.rotation.y = Math.PI
  rear.position.z = -0.012
  g.add(rear)
  const lay = (tex, uv) => {
    front.material.dispose()
    front.material = psx(tex, undefined, uv)
    // a die-cut sticker is its own shape both sides: the picture on the front, white backing paper on the back
    if (die) { rear.material.dispose(); rear.material = psx(tex, undefined, uv, 1) }
  }
  if (sheet) sheet.then((c) => lay(new THREE.CanvasTexture(c))).catch(() => {})
  else new THREE.TextureLoader().load(src, (tex) => lay(tex, coverUv(tex.image, w, h)))
  return g
}

// what goes on the stage, and how it moves
async function buildThing(result, renderer) {
  if (result.kind === 'gift') {
    const gltf = await loadItems(renderer)
    const node = gltf.scene.getObjectByName(NODE_OF[result.code])
    if (!node) return null
    const thing = node.clone(true)
    thing.position.set(0, 0, 0)
    thing.traverse((o) => { if (o.isMesh) o.material = psx(o.material.map, o.material.color?.clone()) })
    const size = new THREE.Box3().setFromObject(thing).getSize(new THREE.Vector3())
    const g = new THREE.Group()
    g.add(thing)
    g.scale.setScalar(1.75 / Math.max(size.x, size.y, size.z))
    return { group: g, move: (o, t) => { o.rotation.y = t * 1.7; o.rotation.x = 0.18 + Math.sin(t * 0.9) * 0.12 } }
  }
  if (result.kind === 'print') {
    const g = card(result.image, 1.3, 1.3 * SHEET, cardBack(), printSheet(result.image, 140))
    g.scale.setScalar(PRINT_SCALE)
    // two quick turns that slow to the print and stop facing you: then the print itself comes up over it (VendingDrop)
    return {
      group: g,
      settle: SETTLE_S,
      move: (o, t) => {
        const k = Math.min(1, t / SETTLE_S)
        o.rotation.y = (1 - (1 - k) ** 3) * Math.PI * 4
        o.rotation.x = k < 1 ? Math.sin(t * 0.8) * 0.06 * (1 - k) : 0
      },
    }
  }
  if (result.kind === 'stickers') {
    const ring = new THREE.Group()
    const designs = result.designs || []
    const back = cardBack()
    designs.forEach((d, i) => {
      const a = (i / Math.max(1, designs.length)) * Math.PI * 2
      const s = card(d.image, 0.78, 0.78, back, null, { die: true })
      s.position.set(Math.sin(a) * 1.25, 0, Math.cos(a) * 1.25)
      s.rotation.y = a
      ring.add(s)
    })
    return { group: ring, move: (o, t) => { o.rotation.y = t * 0.55; o.rotation.x = 0.16 } }
  }
  return null
}

// the chime as it lands: four square notes up, the way a thing was got
function chime() {
  const ctx = getCtx()
  if (!ctx) return
  if (ctx.state === 'suspended') ctx.resume().catch(() => {})
  const now = ctx.currentTime + 0.02
  const out = ctx.createGain()
  out.gain.value = 0.07
  out.connect(ctx.destination)
  ;[523.25, 659.25, 783.99, 1046.5].forEach((f, i) => {
    const o = ctx.createOscillator()
    const g = ctx.createGain()
    o.type = 'square'
    o.frequency.value = f
    const at = now + i * 0.075
    g.gain.setValueAtTime(0, at)
    g.gain.linearRampToValueAtTime(1, at + 0.008)
    g.gain.exponentialRampToValueAtTime(0.001, at + (i === 3 ? 0.6 : 0.16))
    o.connect(g); g.connect(out)
    o.start(at); o.stop(at + 0.65)
  })
}

function Stage({ result, onFailed, onSettled }) {
  const box = useRef(null)
  useEffect(() => {
    // a canvas of its own each time: a context let go on the way out cannot be had again on the same canvas
    const canvas = document.createElement('canvas')
    canvas.style.cssText = 'width:100%;height:100%;display:block;image-rendering:pixelated'
    box.current.appendChild(canvas)
    let renderer
    try {
      renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: false, powerPreference: 'low-power' })
    } catch (err) {
      console.warn('[vending] no WebGL for the drop', err)
      canvas.remove()
      onFailed()
      return undefined
    }
    renderer.setPixelRatio(1)
    renderer.setClearColor(0x000000, 0)
    const scene = new THREE.Scene()
    const camera = new THREE.PerspectiveCamera(32, 1, 0.1, 50)
    camera.position.set(0, 0.15, 5.2)
    camera.lookAt(0, 0, 0)
    let thing = null
    let started = 0
    let raf = 0
    let dead = false
    const fit = () => {
      const r = box.current?.getBoundingClientRect()
      if (!r?.width) return
      const w = Math.max(1, Math.round((BUFFER_H * r.width) / r.height))
      renderer.setSize(w, BUFFER_H, false)
      camera.aspect = w / BUFFER_H
      camera.updateProjectionMatrix()
      // the grid the corners land on: every other pixel of the buffer
      SNAP.set(w / 4, BUFFER_H / 4)
    }
    const frame = (ms) => {
      if (dead) return
      raf = requestAnimationFrame(frame)
      if (!thing) return
      started ||= ms
      const t = (ms - started) / 1000
      // it rises from below with a little overshoot, then floats
      const k = Math.min(1, t / 0.7)
      const rise = 1 + 2.4 * (k - 1) ** 3 + 1.4 * (k - 1) ** 2
      const still = thing.settle ? Math.min(1, t / thing.settle) : 0
      thing.group.position.y = -2.4 * (1 - rise) + Math.sin(t * 2.1) * 0.05 * (1 - still)
      thing.group.scale.setScalar(thing.base * Math.max(0.05, rise))
      thing.move(thing.group, t)
      if (thing.settle && t >= thing.settle && !thing.settled) { thing.settled = true; onSettled?.() }
      renderer.render(scene, camera)
    }
    buildThing(result, renderer).then((built) => {
      if (dead) return
      if (!built) { console.warn('[vending] nothing to draw for', result); onFailed(); return }
      thing = { ...built, base: built.group.scale.x }
      scene.add(built.group)
      fit()
      chime()
    }).catch((err) => { console.warn('[vending] the drop could not draw', err); if (!dead) onFailed() })
    fit()
    raf = requestAnimationFrame(frame)
    window.addEventListener('resize', fit)
    return () => {
      dead = true
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', fit)
      scene.traverse((o) => {
        if (o.geometry) o.geometry.dispose()
        if (o.material) { o.material.uniforms?.map?.value?.dispose?.(); o.material.dispose() }
      })
      renderer.dispose()
      renderer.forceContextLoss?.()
      canvas.remove()
    }
  }, [result, onFailed, onSettled])
  return (
    <div ref={box} style={{ position: 'relative', width: 'min(54vh, 84vw)', height: 'min(54vh, 84vw)' }} />
  )
}

function Spiral() {
  return (
    <svg width="132" height="44" viewBox="0 0 120 40" aria-hidden="true" style={{ display: 'block' }}>
      <g style={{ animation: 'vd-spin 0.5s linear infinite', transformOrigin: '60px 20px', transformBox: 'view-box' }}>
        {Array.from({ length: 7 }, (_, i) => (
          <ellipse key={i} cx={12 + i * 16} cy="20" rx="7" ry="15" fill="none" stroke="#c9d2ff" strokeWidth="3" />
        ))}
      </g>
    </svg>
  )
}

const bare = (s) => String(s || '').replace(/^(a|an|the|some)\s+/i, '')

// what the window says: a little heading, the name, and a line under it
function words(drop) {
  const r = drop.result || {}
  if (drop.status === 'slow') return { head: 'STILL TURNING', name: 'IT IS PAID FOR', line: 'it is taking its time to drop. what dropped will be in your email, and here if you come back.' }
  if (drop.status === 'sorry') return { head: 'SOLD OUT', name: 'NOTHING DROPPED', line: `${r.kind === 'gift' ? 'that row emptied before yours could drop.' : 'the last one went before yours could drop.'} your money is coming back.` }
  if (r.kind === 'gift') {
    const who = r.name && r.name !== 'somebody' ? `it is from ${r.name}` : 'somebody bought it for him'
    return { head: 'FOR PINO', name: bare(r.item).toUpperCase(), line: `it dropped into the tray. he has been told ${who}, and he will find it next time he is at the machine.` }
  }
  if (r.kind === 'print') return { head: 'YOURS', name: `PRINT ${String(r.drawingId || '').toUpperCase()}`, line: `${r.what === 'himself' ? 'one he drew of himself. ' : 'one of his drawings. '}it is yours, and on its way.` }
  if (r.kind === 'stickers') return { head: 'YOURS', name: 'SIX STICKERS', line: 'six of his drawings as stickers. they are yours, and on their way.' }
  return { head: 'DROPPED', name: 'PAID FOR', line: 'it is paid for, and on its way.' }
}

export function VendingDrop({ drop, onClose }) {
  const [flat, setFlat] = useState(false) // no WebGL, or the thing would not load: the window alone
  const failed = useRef(() => setFlat(true)).current
  const [sheet, setSheet] = useState(null) // the print as it comes, sharp, once the card has stopped facing you
  const [landed, setLanded] = useState(false)
  const settled = useRef(() => setLanded(true)).current
  const image = drop.status === 'ready' && drop.result?.kind === 'print' ? drop.result.image : null
  useEffect(() => {
    if (!image) return undefined
    let dead = false
    printSheet(image, 714).then((c) => { if (!dead) setSheet(c.toDataURL('image/jpeg', 0.92)) }).catch(() => {})
    return () => { dead = true }
  }, [image])
  const ready = drop.status === 'ready'
  const said = drop.status !== 'turning' ? words(drop) : null

  useEffect(() => {
    if (drop.status === 'turning') return undefined
    const onKey = (e) => { if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape' || e.key === 'x' || e.key === 'X') { e.preventDefault(); onClose() } }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [drop.status, onClose])

  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'auto', overflow: 'hidden', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'radial-gradient(ellipse at 50% 42%, #1d2d7a 0%, #0b1240 46%, #03040c 100%)', animation: 'vd-fade 0.35s ease both' }}>
      <style>{`
        @keyframes vd-fade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes vd-spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
        @keyframes vd-rays { from { transform: translate(-50%, -50%) rotate(0deg) } to { transform: translate(-50%, -50%) rotate(360deg) } }
        @keyframes vd-flash { from { opacity: 0.85 } to { opacity: 0 } }
        @keyframes vd-shake { 0%, 100% { transform: translate(0, 0) } 20% { transform: translate(-6px, 3px) } 40% { transform: translate(5px, -4px) } 60% { transform: translate(-3px, 2px) } 80% { transform: translate(2px, -1px) } }
        @keyframes vd-window { from { transform: translateY(40px) scaleY(0.1); opacity: 0 } to { transform: translateY(0) scaleY(1); opacity: 1 } }
        @keyframes vd-blink { 0%, 49% { opacity: 1 } 50%, 100% { opacity: 0.25 } }
        @keyframes vd-sharp { from { opacity: 0; filter: blur(3px) brightness(1.6); transform: translate(-50%, -50%) scale(0.97) } to { opacity: 1; filter: none; transform: translate(-50%, -50%) scale(1) } }
        @keyframes vd-twinkle { 0%, 100% { opacity: 0; transform: scale(0.4) } 50% { opacity: 1; transform: scale(1) } }
        @media (prefers-reduced-motion: reduce) { .vd-move { animation: none !important } }
      `}</style>

      {/* scanlines, faint, over everything */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'repeating-linear-gradient(to bottom, rgba(0,0,0,0.18) 0px, rgba(0,0,0,0.18) 1px, transparent 1px, transparent 3px)', zIndex: 3 }} />

      {drop.status === 'turning' && (
        <>
          <Spiral />
          <div style={{ color: '#c9d2ff', marginTop: 14, fontSize: 14 }}>the spiral turns...</div>
          <div style={{ position: 'absolute', right: 28, bottom: 24, color: '#fff', fontWeight: 900, fontStyle: 'italic', letterSpacing: 2, fontSize: 18, textShadow: '2px 2px 0 #1b2a8a', animation: 'vd-blink 1s steps(1) infinite' }}>NOW LOADING</div>
        </>
      )}

      {ready && !flat && (
        <div className="vd-move" style={{ position: 'relative', animation: 'vd-shake 0.32s linear 0.05s both', zIndex: 1 }}>
          {/* light turning behind it */}
          <div className="vd-move" style={{ position: 'absolute', left: '50%', top: '50%', width: '150%', height: '150%', pointerEvents: 'none', borderRadius: '50%', background: 'repeating-conic-gradient(from 0deg, rgba(160,190,255,0.22) 0deg 9deg, transparent 9deg 24deg)', WebkitMaskImage: 'radial-gradient(circle, #000 18%, transparent 68%)', maskImage: 'radial-gradient(circle, #000 18%, transparent 68%)', animation: 'vd-rays 14s linear infinite' }} />
          {[[-38, -30, 0.2], [34, -36, 0.9], [40, 26, 1.5], [-32, 34, 2.1], [4, -46, 2.7]].map(([x, y, d], i) => (
            <div key={i} className="vd-move" style={{ position: 'absolute', left: `calc(50% + ${x}%)`, top: `calc(50% + ${y}%)`, color: '#e6ecff', fontSize: 18, animation: `vd-twinkle 1.6s ease ${d}s infinite both`, pointerEvents: 'none' }}>✦</div>
          ))}
          <Stage result={drop.result} onFailed={failed} onSettled={settled} />
          {/* the print itself, sharp, where the card stopped: what is on its way to them */}
          {landed && sheet && (
            <img src={sheet} alt={`print ${drop.result.drawingId}`} style={{ position: 'absolute', left: '50%', top: '50%', height: `${(PRINT_TALL * 100).toFixed(1)}%`, transform: 'translate(-50%, -50%)', borderRadius: 3, boxShadow: '0 0 0 2px rgba(255,255,255,0.85), 0 10px 30px rgba(0,0,0,0.5)', animation: 'vd-sharp 0.5s ease-out both', pointerEvents: 'none' }} />
          )}
        </div>
      )}
      {ready && flat && sheet && (
        <img src={sheet} alt={`print ${drop.result.drawingId}`} style={{ height: 'min(46vh, 90vw)', borderRadius: 3, boxShadow: '0 0 0 2px rgba(255,255,255,0.85)', zIndex: 1 }} />
      )}
      {ready && <div className="vd-move" style={{ position: 'absolute', inset: 0, background: '#fff', pointerEvents: 'none', animation: 'vd-flash 0.45s ease-out both', zIndex: 2 }} />}

      {said && (
        <div style={{ zIndex: 4, marginTop: ready && !flat ? -8 : 0, width: 'min(520px, 92vw)', padding: '14px 18px 16px', borderRadius: 8, background: 'linear-gradient(160deg, #3d55c4 0%, #1a2a8c 45%, #0a1252 100%)', border: '2px solid #e8ecff', boxShadow: 'inset 0 0 0 2px #5b6bb0, inset 0 0 0 3px #0a1252, 0 6px 0 rgba(0,0,0,0.45)', color: '#fff', textShadow: '1px 1px 0 #0a0e2e, 2px 2px 0 rgba(0,0,0,0.35)', animation: `vd-window 0.28s cubic-bezier(.2,1.3,.4,1) ${ready && !flat ? 0.75 : 0.1}s both` }}>
          <div style={{ color: '#ffe66b', fontWeight: 800, fontSize: 12, letterSpacing: 2 }}>{said.head}</div>
          <div style={{ fontWeight: 900, fontSize: 24, letterSpacing: 1, margin: '2px 0 6px' }}>{said.name}</div>
          <div style={{ fontSize: 14, lineHeight: 1.4, color: '#e6ebff' }}>{said.line}</div>
        </div>
      )}

      {said && (
        <button onClick={onClose} style={{ position: 'absolute', right: 24, bottom: 22, zIndex: 4, display: 'flex', alignItems: 'center', gap: 8, background: 'transparent', border: 'none', color: '#fff', fontWeight: 800, fontSize: 16, letterSpacing: 1, cursor: 'pointer', textShadow: '1px 1px 0 #0a0e2e', animation: `vd-fade 0.3s ease ${ready && !flat ? 1.1 : 0.3}s both` }}>
          <span aria-hidden="true" style={{ width: 26, height: 26, borderRadius: '50%', background: 'radial-gradient(circle at 35% 30%, #4a4f63, #15171f)', border: '1px solid #000', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#7fb0ff', fontSize: 15, fontWeight: 900 }}>✕</span>
          OK
        </button>
      )}
    </div>
  )
}
